import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const KinRegister = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const prefillEmail = searchParams.get('email') || '';
  const { register, login, setUser } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [addressValid, setAddressValid] = useState(null);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [distance, setDistance] = useState(null);

  useEffect(() => {
    if (code) {
      fetchInvitation();
    } else {
      setPageLoading(false);
      setError('No invitation code found');
    }
  }, [code]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(`/api/kin/invite/${code}`);
      if (!response.ok) {
        throw new Error('Failed to load invitation');
      }
      const data = await response.json();
      setInvitation(data);
      if (!prefillEmail) {
        setEmail(data.invitedEmail || '');
      }
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('Failed to load invitation. Please check your link and try again.');
    } finally {
      setPageLoading(false);
    }
  };

  const checkAddress = async () => {
    if (!address || !suburb || !state || !postcode) {
      toast.error('Please fill in your full address');
      return;
    }

    setCheckingAddress(true);
    try {
      const query = `${address}, ${suburb}, ${state} ${postcode}`;
      const response = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const coords = [parseFloat(data[0].lon), parseFloat(data[0].lat)];
          setCoordinates(coords);
          
          if (invitation?.kin) {
            const kinCoords = invitation.kin.location.coordinates;
            const dist = calculateDistance(coords, kinCoords);
            setDistance(dist);
            const maxRadius = invitation.kin.maxRadiusKm || 50;
            
            if (dist <= maxRadius) {
              setAddressValid(true);
              toast.success(`✅ You're within the Kin's service area! (${dist.toFixed(1)}km from centre)`);
            } else {
              setAddressValid(false);
              toast.warning(`📍 You're ${dist.toFixed(1)}km from the Kin centre. Maximum allowed is ${maxRadius}km.`);
            }
          }
        } else {
          toast.error('Address not found. Please check and try again.');
        }
      } else {
        toast.error('Failed to validate address');
      }
    } catch (err) {
      console.error('Error validating address:', err);
      toast.error('Failed to validate address');
    } finally {
      setCheckingAddress(false);
    }
  };

  const calculateDistance = (coords1, coords2) => {
    const R = 6371;
    const dLat = (coords2[1] - coords1[1]) * Math.PI / 180;
    const dLon = (coords2[0] - coords1[0]) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(coords1[1] * Math.PI / 180) * Math.cos(coords2[1] * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (addressValid !== true || !coordinates) {
      setError('Please validate your address first');
      return;
    }

    setLoading(true);
    try {
      // 1. Register the user with pre-verified email
      const userData = {
        name: name.trim(),
        email,
        password,
        role: 'buyer',
        emailVerified: true,
        location: {
          address: address,
          suburb: suburb,
          state: state,
          postcode: postcode,
          coordinates: coordinates
        }
      };

      const response = await register(userData);
if (!response || !response.success) {
  // Check if we got a user anyway (might be a partial success)
  const token = localStorage.getItem('token');
  if (!token) {
    setError('Registration failed. Please try again.');
    setLoading(false);
    return;
  }
  console.log('⚠️ Registration response said false but token exists, continuing...');
}

      // 2. Get token (from response or localStorage)
      let token = response.token || localStorage.getItem('token');
      
      // 3. If no token, try logging in
      if (!token) {
        const loginSuccess = await login(email, password);
        if (!loginSuccess) {
          setError('Login failed. Please try logging in manually.');
          setLoading(false);
          return;
        }
        token = localStorage.getItem('token');
      }

      if (!token) {
        setError('Authentication failed. Please try logging in manually.');
        setLoading(false);
        return;
      }

      // 4. Accept the Kin invitation
      const acceptResponse = await fetch(`/api/kin/invite/${code}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!acceptResponse.ok) {
        const data = await acceptResponse.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }

      // 5. Refresh user data
      const meResponse = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (meResponse.ok) {
        const userData = await meResponse.json();
        setUser(userData);
      }

      toast.success('🎉 Welcome to your Kin!');
      navigate('/kin-dashboard');

    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">😕</div>
          <h1 className="text-xl font-bold mb-2">Error Loading Invitation</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="bg-green-600 text-white px-6 py-2 rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h1 className="text-xl font-bold mb-2">Invitation Not Found</h1>
          <p className="text-gray-600 mb-4">This invitation may have expired.</p>
          <button onClick={() => navigate('/')} className="bg-green-600 text-white px-6 py-2 rounded-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">👋</div>
          <h1 className="text-2xl font-bold text-gray-800">Join {invitation.kin?.name}</h1>
          <p className="text-gray-600 text-sm mt-1">
            {invitation.invitedBy?.name} invited you to join their Kin
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            📧 Registering with: <strong>{email}</strong>
          </p>
          <p className="text-xs text-blue-600 mt-1">
            This is the email your invitation was sent to.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Jenny Purchase"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              📍 Your Address
            </p>
            
            <div className="space-y-3">
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                  placeholder="Suburb"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <input
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="Postcode"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
              
              <button
                type="button"
                onClick={checkAddress}
                disabled={checkingAddress}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {checkingAddress ? 'Checking...' : '✅ Check Address'}
              </button>

              {addressValid === true && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-sm text-green-700">
                  ✅ You're within the Kin's service area! ({distance?.toFixed(1)}km from centre)
                </div>
              )}
              {addressValid === false && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-700">
                  ❌ You're {distance?.toFixed(1)}km from the Kin centre. Maximum allowed is {invitation.kin?.maxRadiusKm || 50}km.
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !addressValid}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : '✅ Join Kin & Create Account'}
          </button>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-xs text-blue-700">
              📋 <strong>What happens next:</strong>
            </p>
            <ol className="text-xs text-blue-700 list-decimal list-inside mt-1 space-y-1">
              <li>Your account will be created and you'll be logged in immediately</li>
              <li>You'll be added to the Kin</li>
              <li>Complete your 4 courses to unlock ordering</li>
            </ol>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KinRegister;
